import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import apiClient, { changeCredentials } from './client';
import useAuthStore, { selectPrincipal } from 'stores/authStore';

export const accountKeys = { status: ['account-status'], sessions: ['account-sessions'] };

const current = () => selectPrincipal(useAuthStore.getState());
const ensureCurrent = (principal) => {
  if (current() !== principal) throw new axios.CanceledError('Authentication context changed.');
};

export function useAccount() {
  return useQuery({ queryKey: accountKeys.status, queryFn: () => apiClient.get('/api/v1/auth/account/').then((r) => r.data), staleTime: 0 });
}

export function useAccountSessions() {
  return useQuery({ queryKey: accountKeys.sessions, queryFn: () => apiClient.get('/api/v1/auth/account/sessions/').then((r) => r.data), staleTime: 0 });
}

export async function saveProfile(name) {
  const principal = current();
  const { data } = await apiClient.patch('/api/v1/auth/me/', { name });
  ensureCurrent(principal);
  useAuthStore.getState().updateUser({ id: data.id, name: data.name, email: data.email });
  return data;
}

// Sensitive inputs and results stay out of TanStack mutation/query caches.
export async function accountAction(action, proof) {
  const principal = current();
  const { data } = await apiClient.post(`/api/v1/auth/account/${action}/`, proof);
  ensureCurrent(principal);
  if (data.email) useAuthStore.getState().updateUser({ email: data.email });
  return data;
}

export async function savePassword(proof) {
  return changeCredentials(async () => {
    const principal = current();
    const { data } = await apiClient.post('/api/v1/auth/password/change/', proof, { credentialChange: true });
    ensureCurrent(principal);
    useAuthStore.getState().updateTokens({ accessToken: data.access, refreshToken: data.refresh });
    return data;
  });
}

// Abortable, uncached feedback; a preflight never grants change authorization.
export async function validatePassword(newPassword, signal) {
  const principal = current();
  const { data } = await apiClient.post('/api/v1/auth/account/password/validate/', { new_password: newPassword }, { signal });
  ensureCurrent(principal);
  return data;
}

export async function preparePassword(proof) {
  return accountAction('password/prepare', proof);
}
